import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { getGlobalFeatureToggle } from '@makemydeal/dr-shared-ui-utils';
import { scenarioHydrateReceived } from './asyncScenarioLoadActionCreators';
import * as multiScenarioSelectors from '../selectors/multiScenarioSelectors';

import { handleAioDxgEvents } from './socketHandlers/aioDxgEvents';
import { handleCopyCreditApp } from './socketHandlers/copyCreditApp';
import { handleDrContracting } from './socketHandlers/drContracting';
import { handleGenericPath } from './socketHandlers/genericPath';
import { handleMvDocMove } from './socketHandlers/mvDocMove';
import { handleMvPushToDms } from './socketHandlers/mvPushToDms';
import { handlePurchaseHistory, PURCHASE_HISTORY_TOPIC } from './socketHandlers/purchaseHistory';
import { handlePushToFI } from './socketHandlers/pushToFI';
import { handleTradeInIntelligence, TRADE_IN_INTELLIGENCE_TOPIC } from './socketHandlers/tradeInIntelligence';
import type { EventReceived } from './socketHandlers/types';

export type { EventReceived };

export const dispatchEvent = (state: StateTree, event: any): EventReceived => {
    // Emit the correlation id forwarded in the WebSocket payload so the receive side can be
    // joined to the originating deal-save trace in New Relic. Discrete per-message page action;
    // only fired for frames that carry a payload (skips subscribe-ack frames that never have an id).
    if (event.data) {
        logNewRelicPageAction('MV:WebSocketReceived', {
            coxAutoCorrelationId: event.data['X-CoxAuto-Correlation-Id'],
            eventTopic: event.topic
        });
    }

    let topicResult: EventReceived | null = null;
    switch (event.topic) {
        case 'drContracting':
            topicResult = handleDrContracting(state, event);
            break;
        case 'copyCreditApp':
            topicResult = handleCopyCreditApp(state, event);
            break;
        case 'pushToFI':
            topicResult = handlePushToFI(state, event);
            break;
        case 'mv_pushToDms':
            topicResult = handleMvPushToDms(state, event);
            break;
        case 'mv_docMove':
            topicResult = handleMvDocMove(state, event);
            break;
        case PURCHASE_HISTORY_TOPIC:
            topicResult = handlePurchaseHistory(state, event);
            break;
        case TRADE_IN_INTELLIGENCE_TOPIC:
            topicResult = handleTradeInIntelligence(state, event);
            break;
        case 'aioDxgEvents':
            topicResult = handleAioDxgEvents(state, event);
            break;
        case 'drMultiScenarioLoad':
            if (getGlobalFeatureToggle('enableMVMSLoadAsync') && !event.message?.includes('subscribe')) {
                const activeComparison = multiScenarioSelectors.getActiveComparison(state);
                topicResult = scenarioHydrateReceived(event.data?.scenarios?.[0], {
                    activeComparison,
                    errors: event.data?.errors
                }) as EventReceived;
            }
            break;
    }

    if (topicResult) {
        return topicResult;
    } else {
        return handleGenericPath(state, event);
    }
};
